// Libraries
import { useEffect } from "react";

// Components
import PayrollMappingContainer from "./PayrollMappingContainer";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { useLocationId } from "@/Common/hooks/Location/useLocationId";

// Subscriptions
import {
	onUpdateCustomFieldDefinitions,
	onUpdateMappedCustomFields,
	onUpdateMappedPayItems,
	onUpdatePayItemsOptions,
} from "@/Settings/api/PayrollMappings/subscriptions";

// Layouts
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";

// Constants
import { PAYROLL_MAPPING_KEYS } from "@/Settings/Constants/PayrollMapping";

// Context
import { PayrollMappingsContextProvider } from "@/Settings/context/PayrollMapping";

// Constants
const QUERY_IDENTIFIERS = Object.values(PAYROLL_MAPPING_KEYS.QUERY);

export default function PayrollMappingLayer() {
	const { clearCache } = useCacheCleaner();
	const { addSubscription, deleteSubscription } = useSubscriptions();
	const locationId = useLocationId();

	useEffect(() => {
		if (locationId) {
			addSubscription(
				[PAYROLL_MAPPING_KEYS.SUBSCRIPTION.MAPPED_PAY_ITEMS, locationId],
				[
					{
						context: "onUpdateMappedPayItems",
						query: onUpdateMappedPayItems,
						variables: {
							locationId,
						},
					},
				]
			);

			addSubscription(
				[PAYROLL_MAPPING_KEYS.SUBSCRIPTION.MAPPED_CUSTOM_FIELDS, locationId],
				[
					{
						context: "onUpdateMappedCustomFields",
						query: onUpdateMappedCustomFields,
						variables: {
							locationId,
						},
					},
				]
			);
			addSubscription(
				[PAYROLL_MAPPING_KEYS.SUBSCRIPTION.CUSTOM_FIELD_DEFINITIONS, locationId],
				[
					{
						context: "onUpdateCustomFieldDefinitions",
						query: onUpdateCustomFieldDefinitions,
						variables: {
							locationId,
						},
					},
				]
			);
			addSubscription(
				[PAYROLL_MAPPING_KEYS.SUBSCRIPTION.PAY_ITEMS_OPTIONS, locationId],
				[
					{
						context: "onUpdatePayItemsOptions",
						query: onUpdatePayItemsOptions,
						variables: {
							locationId,
						},
					},
				]
			);
		}
		return () => {
			deleteSubscription([PAYROLL_MAPPING_KEYS.SUBSCRIPTION.MAPPED_PAY_ITEMS, locationId]);
			deleteSubscription([PAYROLL_MAPPING_KEYS.SUBSCRIPTION.MAPPED_CUSTOM_FIELDS, locationId]);
			deleteSubscription([PAYROLL_MAPPING_KEYS.SUBSCRIPTION.CUSTOM_FIELD_DEFINITIONS, locationId]);
			deleteSubscription([PAYROLL_MAPPING_KEYS.SUBSCRIPTION.PAY_ITEMS_OPTIONS, locationId]);
			clearCache(QUERY_IDENTIFIERS);
		};
	}, [locationId]);

	return (
		<PayrollMappingsContextProvider locationId={locationId}>
			<BodyLayer>
				<RoundedBox sx={{ minHeight: "630px", position: "relative" }} className="!px-0">
					<PayrollMappingContainer />
				</RoundedBox>
			</BodyLayer>
		</PayrollMappingsContextProvider>
	);
}
